import "ag-grid-enterprise"

import {
  DEFAULT_CUBE_PAGE_LIMIT,
  DEFAULT_SCROLL_DEBOUNCE,
  MAX_CUBE_PAGE_LIMIT,
} from "../../constants"
import { GridOptions, LicenseManager } from "ag-grid-enterprise"
import { RefObject, useCallback, useMemo, useState } from "react"
import {
  ReportPayload,
  excelStyles,
  getColumnDefs,
  getNodeRoute,
} from "../../utils"
import { Role, useAuthenticated } from "providers/A12n"
import { View, getQueryByView } from "./config"

import { AgGridReact } from "ag-grid-react"
import LoadTemplateModal from "../LoadTemplateModal"
import { Loading } from "components/Loading"
import SaveTemplateModal from "../SaveTemplateModal"
import { dataSource } from "../../api"
import { useCubeMeta } from "@cubejs-client/react"
import { TablePagination } from "@material-ui/core"
import { AgGridCustomPaginationActions } from "../AgGridCustomPaginationActions"

LicenseManager.setLicenseKey(
  "Using_this_AG_Grid_Enterprise_key_( AG-042876 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Logicea, LLC )_is_granted_a_( Single Application )_Developer_License_for_the_application_( Halogen )_only_for_( 1 )_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_( Halogen )_need_to_be_licensed___( Halogen )_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 23 May 2024 )____[v2]_MTcxNjQxODgwMDAwMA==05ac1de5389eeee14509def26b01a8e5",
)

const style = { height: "80vh", width: "100%" }

const perRoleView = {
  BUYER: "buying" as View,
  BILLER: "billing" as View,
  PLANNER: "planning" as View,
  AGENCY_FINANCE: "buying" as View,
}

type Props = {
  userRole?: Role
  gridRef?: RefObject<AgGridReact>
  modalStates: {
    saveTemplate: {
      open: boolean
    }
    loadTemplate: {
      open: boolean
    }
  }
  resetState: () => void
  setExportPayload: (value: ReportPayload) => void
}

const gridOptions: GridOptions = {
  defaultColDef: {
    flex: 1,
    minWidth: 180,
    sortable: true,
    resizable: true,
  },
  sideBar: ["columns", "filters"],
  suppressFieldDotNotation: true,
  rowModelType: "serverSide",
  cacheBlockSize: DEFAULT_CUBE_PAGE_LIMIT,
  blockLoadDebounceMillis: DEFAULT_SCROLL_DEBOUNCE,
  pagination: true,
  suppressPaginationPanel: true,
  rowBuffer: 50,
  excelStyles,
}

export const ReportingGrid = (props: Props) => {
  const { gridRef, userRole, modalStates, resetState, setExportPayload } = props
  const { meta } = useAuthenticated()

  const [paginationData, setPaginationData] = useState({
    currentPage: 0,
    totalPages: 0,
    isTotalPagesFetched: false,
    totalRecords: 0,
  })

  const { timeZone = "Etc/UTC" } = meta ?? {}
  const cubeMeta = useCubeMeta()
  const metaCubes = cubeMeta?.response?.cubes

  const view = perRoleView[userRole as keyof typeof perRoleView]

  const query = useMemo(
    () => getQueryByView(view, metaCubes),
    [view, metaCubes],
  )

  const onGridReady = useCallback(() => {
    const ds = dataSource(
      query,
      timeZone,
      (params) =>
        setExportPayload({
          params,
          query,
        }),
      metaCubes,
    )
    gridOptions.api?.setServerSideDatasource(ds)
  }, [metaCubes, query, setExportPayload, timeZone])

  const onChange = useCallback(() => {
    gridOptions.api?.refreshServerSide({ purge: false })
    gridRef?.current?.api.forEachNode(function (node) {
      if (node.group) {
        const route = getNodeRoute(node)
        gridOptions.api?.refreshServerSide({
          route: route,
          purge: false,
        })
      }
    })
  }, [gridRef])

  const isLoading = !metaCubes

  const columnDefs = useMemo(
    () => getColumnDefs(query, timeZone, metaCubes),
    [metaCubes, query, timeZone],
  )

  const onPaginationChanged = useCallback(() => {
    setPaginationData({
      currentPage: gridRef?.current?.api?.paginationGetCurrentPage() ?? 0,
      totalPages: gridRef?.current?.api?.paginationGetTotalPages() ?? 0,
      isTotalPagesFetched: !!gridRef?.current?.api?.paginationIsLastPageFound(),
      totalRecords: gridRef?.current?.api?.paginationGetRowCount() ?? 0,
    })
  }, [gridRef])

  const onChangeRowsPerPage = useCallback(
    (evt) => {
      gridRef?.current?.api.paginationSetPageSize(parseInt(evt.target.value))
      gridRef?.current?.api.setCacheBlockSize(parseInt(evt.target.value))
      gridOptions.api?.refreshServerSide({ purge: false })
    },
    [gridRef],
  )

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="ag-theme-alpine" style={style}>
      <AgGridReact
        gridOptions={gridOptions}
        ref={gridRef}
        onGridReady={onGridReady}
        onFilterChanged={onChange}
        columnDefs={columnDefs}
        onColumnPivotModeChanged={(props) => {
          if (props.columnApi?.isPivotMode()) {
            props.api.setCacheBlockSize(MAX_CUBE_PAGE_LIMIT)
          } else {
            props.columnApi.setPivotResultColumns([])
            props.api.setCacheBlockSize(DEFAULT_CUBE_PAGE_LIMIT)
          }
        }}
        onPaginationChanged={onPaginationChanged}
      />
      <TablePagination
        component="div"
        classes={{ caption: "BodyBody-sDark-greyRegular" }}
        page={paginationData.currentPage}
        count={paginationData.totalRecords}
        rowsPerPageOptions={[100, 200, 500, 1000]}
        rowsPerPage={gridRef?.current?.api.paginationGetPageSize() ?? 0}
        onChangePage={() => null}
        onChangeRowsPerPage={onChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          `${from} - ${to} of ${
            paginationData.isTotalPagesFetched ? count : "more"
          }`
        }
        ActionsComponent={(props) => (
          <AgGridCustomPaginationActions
            paginationData={paginationData}
            gridRef={gridRef}
            {...props}
          />
        )}
      />
      <SaveTemplateModal
        onClose={() => (modalStates.saveTemplate.open = false)}
        gridRef={gridRef!}
      />
      <LoadTemplateModal
        onClose={() => (modalStates.loadTemplate.open = false)}
        resetState={resetState}
        gridRef={gridRef!}
      />
    </div>
  )
}
